import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  useGetIntakeExportStatusQuery,
  useStartIntakeExportMutation,
} from '../services/api/intakeService';
import type { ExportStatusResponse } from '../services/types/intake-types';

interface UseIntakeExportProps {
  intakeId: string | null;
}

const POLLING_INTERVAL_MS = 5000;
const FAILSAFE_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

export const useIntakeExport = ({ intakeId }: UseIntakeExportProps) => {
  const [status, setStatus] = useState<ExportStatusResponse['status'] | null>(
    null,
  );
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [downloadTimestamp, setDownloadTimestamp] = useState<string | null>(
    null,
  );

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevStatusRef = useRef<ExportStatusResponse['status'] | null>(null);

  // RTK Query hooks
  const [startExport] = useStartIntakeExportMutation();

  const { data: statusData, refetch: refetchStatus } =
    useGetIntakeExportStatusQuery(intakeId ?? (skipToken as any), {
      skip: !intakeId,
      // Enable/disable polling dynamically
      pollingInterval: isPolling ? POLLING_INTERVAL_MS : undefined,
      refetchOnMountOrArgChange: true,
    });

  // Helpers
  const clearFailsafe = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Effects
  // React to backend status changes
  useEffect(() => {
    if (!statusData) return;

    const { status: newStatus, download_url, created_at } = statusData;
    setStatus(newStatus);
    setDownloadUrl(download_url ?? null);
    setDownloadTimestamp(created_at ?? null);

    // Auto-start polling if we detect an active export the UI didn't trigger
    if (!isPolling && (newStatus === 'PENDING' || newStatus === 'GENERATING')) {
      setIsPolling(true);
    }

    // Stop polling & clear failsafe on terminal states
    if (newStatus === 'COMPLETED' || newStatus === 'FAILED') {
      clearFailsafe();
      if (isPolling) setIsPolling(false);

      // Only surface toast when we were previously in an in-progress state
      const wasProcessing =
        prevStatusRef.current === 'PENDING' ||
        prevStatusRef.current === 'GENERATING';

      if (wasProcessing) {
        if (newStatus === 'COMPLETED') {
          toast.success('Export completed! You can now download the PDF.');
        } else {
          toast.error('Export failed. Please try again.');
        }
      }
    }

    // update prevStatus for next render
    prevStatusRef.current = newStatus;
  }, [statusData, isPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearFailsafe();
    };
  }, []);

  // Actions
  const handleStartExport = useCallback(async () => {
    if (!intakeId) {
      toast.error('No intake ID available');
      return;
    }

    setError(null);

    try {
      // Optimistically set state to in-progress so UI disables export instantly
      setStatus('PENDING');
      setDownloadUrl(null);
      setDownloadTimestamp(null);
      setIsPolling(true);

      const response = await startExport(intakeId).unwrap();

      // Immediately ask for the latest status instead of waiting for first
      // polling interval so the UI updates sooner and users see the request
      // in the Network tab.
      refetchStatus().catch(() => {});

      // Ensure we pick up whatever exact in-progress status backend returned
      setStatus(response.status);

      // Failsafe timeout
      clearFailsafe();
      timeoutRef.current = setTimeout(() => {
        setIsPolling(false);
        toast.error(
          'Export is taking longer than expected. Please refresh the page to check status.',
        );
      }, FAILSAFE_TIMEOUT_MS);

      toast.success('Export started successfully!');
    } catch (err: any) {
      const errorMessage = err?.data?.error || 'Failed to start export';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  }, [intakeId, startExport, refetchStatus]);

  const handleDownload = useCallback(() => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  }, [downloadUrl]);

  // Manual polling fallback – guarantees refetch even if RTK Query polling is
  // disabled externally (e.g., browser tab lost focus). This sets up a
  // setInterval while isPolling is true and cleans it up otherwise.
  useEffect(() => {
    if (!isPolling) return undefined;

    const id = setInterval(() => {
      refetchStatus().catch(() => {});
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(id);
  }, [isPolling, refetchStatus]);

  // Derived booleans
  const isProcessing = status === 'PENDING' || status === 'GENERATING';

  const canExport = !!intakeId && !isProcessing;
  const canDownload = status === 'COMPLETED' && Boolean(downloadUrl);
  const exportFailed = status === 'FAILED';

  return {
    // State
    isPolling,
    status,
    canExport,
    canDownload,
    isProcessing,
    exportFailed,
    downloadTimestamp,
    error,

    // Actions
    handleStartExport,
    handleDownload,
  } as const;
};
